import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Section, Toggle } from '../PageShell.tsx';
import { api } from '../../lib/api.ts';
import { SaveRow, saveFailureMessage, useSaved } from './shared.tsx';
import type { SettingsDto } from './shared.tsx';

/**
 * Workspace-wide notification switches.
 *
 * Both are the same complaint: a message per enquiry, per agent, with no way to
 * stop it. A workspace that watches its inbox all day, or that routes these
 * somewhere else, wants neither — and turning one off is not the same decision
 * as turning off the other, so they are two switches rather than one.
 *
 * Nothing is lost either way. The conversation still opens, the submission is
 * still captured and still listed; only the email stops.
 */
export function SettingsTab({ settings }: { settings: SettingsDto }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [saved, markSaved] = useSaved();
  const [error, setError] = useState<string | null>(null);

  const [agentsWhenOffline, setAgentsWhenOffline] = useState(
    settings.chat.notifications?.agentsWhenOffline ?? true,
  );
  const [capturedForms, setCapturedForms] = useState(
    settings.chat.notifications?.capturedForms ?? true,
  );
  const [unanswered, setUnanswered] = useState(settings.chat.notifications?.unanswered ?? true);

  const save = useMutation({
    mutationFn: () =>
      api.put('/settings/notifications', {
        notifyAgentsWhenOffline: agentsWhenOffline,
        notifyCapturedForms: capturedForms,
        notifyUnanswered: unanswered,
      }),
    onSuccess: () => {
      setError(null);
      markSaved();
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (caught: unknown) => {
      setError(saveFailureMessage(caught));
    },
  });

  return (
    <Section
      title="Email notifications"
      description="What the team is told about, outside the app."
    >
      <div className="space-y-3">
        <Toggle
          label="Email the team when nobody is online"
          description="A customer writing in while no agent is connected emails you and everyone you invited, with a link to the conversation. Off, the message waits in the inbox until somebody opens it."
          checked={agentsWhenOffline}
          onChange={setAgentsWhenOffline}
        />
        <Toggle
          label="Email the team about conversations nobody answered"
          description="A conversation where the customer spoke last and nobody has replied is chased once, and once only, until they write again. It goes to whoever it is assigned to, or to everybody if it is still in the queue."
          checked={unanswered}
          onChange={setUnanswered}
        />
        <Toggle
          label="Email the team about unmatched form submissions"
          description="A tracked-form submission that opens a conversation announces itself through that conversation. This is only the rest: one that matched no form, or carried neither an email address nor a phone number. Off, it is still captured and still listed under Tracked forms."
          checked={capturedForms}
          onChange={setCapturedForms}
        />
      </div>

      <SaveRow
        pending={save.isPending}
        saved={saved}
        error={error}
        onSave={() => {
          save.mutate();
        }}
      />
    </Section>
  );
}
